import type { Chat } from "@ai-sdk/react";
import type { UIMessage } from "ai";
import type { ThreadId } from "./thread";

/**
 * One `Chat` per thread, owned outside React (MIN-268 — moved here from
 * `apps/provider/src/lib/agent-chat/chat-registry.ts` so `apps/ai` sits behind
 * the same registry).
 *
 * Any surface — a panel, a floating prompt, a full-page route — that builds
 * its own `Chat` per mount and reads `messages` only at construction time
 * freezes whatever the server had at that instant: whichever surface mounts
 * mid-stream never catches up, and only a reload (a fresh mount with a current
 * seed) recovers. Keying the instance by thread id and injecting it via
 * `useChat({ chat })` makes the conversation outlive every mount, so surfaces
 * are views onto one stream rather than racing copies of it.
 *
 * No I/O and no React here on purpose: this module only decides identity and
 * lifetime. Constructing a `Chat` needs a transport, auth and a query client,
 * so callers pass a factory and this never learns what a thread actually
 * talks to.
 */

interface RegistryEntry {
  chat: Chat<UIMessage>;
  /** Has the server's history been folded in? Guards against a late refetch. */
  hydrated: boolean;
}

const entries = new Map<ThreadId, RegistryEntry>();

/**
 * The thread's `Chat`, building it via `create` on first ask. Every later
 * caller — every other surface — gets that same instance.
 */
export function getThreadChat(threadId: ThreadId, create: () => Chat<UIMessage>): Chat<UIMessage> {
  const existing = entries.get(threadId);
  if (existing) return existing.chat;

  const chat = create();
  entries.set(threadId, { chat, hydrated: false });
  return chat;
}

/**
 * Fold the server's persisted history into a thread's `Chat`, once.
 *
 * Returns whether it actually wrote. Because the `Chat` now outlives its
 * mounts, live state is authoritative and a seed arriving late must lose:
 * both an already-hydrated thread and one that has local messages (the first
 * message of a brand-new thread, sent before its empty seed resolved) are
 * left alone. They're still marked hydrated, so a stale seed can't come back
 * around and win later.
 */
export function hydrateThreadChat(threadId: ThreadId, messages: UIMessage[]): boolean {
  const entry = entries.get(threadId);
  if (!entry || entry.hydrated) return false;

  entry.hydrated = true;
  if (entry.chat.messages.length > 0) return false;

  entry.chat.messages = messages;
  return true;
}

export function isThreadChatHydrated(threadId: ThreadId): boolean {
  return entries.get(threadId)?.hydrated ?? false;
}

/**
 * Forget a thread — call it when the thread is deleted, so a recycled id can
 * never inherit the dead conversation.
 */
export function evictThreadChat(threadId: ThreadId): void {
  entries.delete(threadId);
}

/** Drop every thread. For tests, and for tearing down on sign-out. */
export function resetChatRegistry(): void {
  entries.clear();
}
